/**
 * Inline translation helper for the attention queue.
 *
 * Agent output in the queue (App Store review text, proposed replies) is
 * frequently in the customer's language - Japanese, Chinese, Spanish, etc.
 * Rather than right-clicking and leaning on macOS's native translation, the
 * operator can flip the whole card to English. This module owns:
 *
 *   - `translateText`     - translate a single segment
 *   - `translateTexts`    - translate many segments in one batch round-trip
 *                           (used by the card-level translate control, which
 *                           collapses every translatable field on a card into
 *                           a single upstream request)
 *   - a session-scoped cache so re-opening an item is instant and free
 *   - `looksTranslatable` - a cheap heuristic to decide whether a field is
 *                           worth offering for translation at all
 *
 * The card-level state machine that ties these together lives in
 * `components/approval/CardTranslation.tsx`.
 *
 * Backend: POST /v1/text/translate (Google Cloud Translation proxy). We only
 * ever translate plain text - HTML is never sent to the translate endpoint.
 */

import { api } from '@/lib/api'

const TRANSLATE_PATH = '/v1/text/translate'
const DEFAULT_TARGET = 'en'

export interface TranslateResult {
  translatedText: string
  detectedSourceLanguage: string
  targetLanguage: string
}

interface TranslateApiResponse {
  translated_text: string
  detected_source_language?: string
  target_language: string
}

interface TranslateBatchSegment {
  translated_text: string
  detected_source_language?: string
}

interface TranslateBatchApiResponse {
  translations?: TranslateBatchSegment[]
  target_language: string
}

/**
 * Options for {@link translateText}. All optional - the defaults (auto-detect
 * the source, translate to English, treat input as plain text) match what the
 * attention queue needs, but callers can override any of them: pin the source
 * language to skip detection, choose a non-English target, or pass HTML.
 */
export interface TranslateOptions {
  /** Target language code. Defaults to "en". */
  target?: string
  /** Source language hint. Omit to auto-detect. */
  source?: string
  /** Content format. Defaults to "text". */
  format?: 'text' | 'html'
  /**
   * Optional AbortSignal. When the caller aborts (e.g. the card unmounts or
   * the operator flips back to the original before the batch resolves), the
   * in-flight request is cancelled so we don't burn translate quota on a
   * result nobody will read.
   */
  signal?: AbortSignal
}

/**
 * Session-scoped cache. A translation is deterministic for a given
 * (text, target, source, format) tuple, so the key includes all four to avoid
 * collisions when the same text is translated with different options. Caching
 * avoids repeat network calls and cost when the operator toggles back and forth
 * or reopens the same queue item. Cleared naturally when the app is closed.
 */
const cache = new Map<string, TranslateResult>()

function cacheKey(text: string, target: string, source: string, format: string): string {
  return `${target}|${source}|${format}|${text}`
}

/**
 * Translate `text` using the given options, going through the cache. Defaults:
 * auto-detect source, translate to English, plain-text format.
 */
export async function translateText(text: string, opts: TranslateOptions = {}): Promise<TranslateResult> {
  const target = opts.target ?? DEFAULT_TARGET
  const source = opts.source ?? ''
  const format = opts.format ?? 'text'

  const key = cacheKey(text, target, source, format)
  const cached = cache.get(key)
  if (cached) return cached

  const body: Record<string, unknown> = { text, target_language: target, format }
  if (source) body.source_language = source

  const resp = await api.post<TranslateApiResponse>(TRANSLATE_PATH, body, opts.signal)

  const result: TranslateResult = {
    translatedText: resp.translated_text,
    detectedSourceLanguage: resp.detected_source_language ?? '',
    targetLanguage: resp.target_language,
  }
  cache.set(key, result)
  return result
}

/**
 * Translate many segments in one batch request, preserving input order. Used by
 * the card-level translate control so flipping a card to English collapses all
 * of its translatable fields (review text, proposed reply, reviewer nickname,
 * inbound email body, ...) into a single upstream round-trip.
 *
 * Already-cached segments are served from the cache and excluded from the
 * network request; only the cache misses are sent. The returned array lines up
 * 1:1 with `texts`. On a per-segment gap from the backend, the original text is
 * returned for that slot so the caller always gets a usable string.
 */
export async function translateTexts(texts: string[], opts: TranslateOptions = {}): Promise<TranslateResult[]> {
  const target = opts.target ?? DEFAULT_TARGET
  const source = opts.source ?? ''
  const format = opts.format ?? 'text'

  const results: (TranslateResult | undefined)[] = texts.map((t) =>
    cache.get(cacheKey(t, target, source, format)),
  )

  const missingIdx: number[] = []
  results.forEach((r, i) => {
    if (!r) missingIdx.push(i)
  })

  if (missingIdx.length > 0) {
    const missingTexts = missingIdx.map((i) => texts[i])
    const body: Record<string, unknown> = { texts: missingTexts, target_language: target, format }
    if (source) body.source_language = source

    const resp = await api.post<TranslateBatchApiResponse>(TRANSLATE_PATH, body, opts.signal)
    const segments = resp.translations ?? []

    missingIdx.forEach((origIdx, j) => {
      const seg = segments[j]
      const result: TranslateResult = {
        translatedText: seg?.translated_text ?? texts[origIdx],
        detectedSourceLanguage: seg?.detected_source_language ?? '',
        targetLanguage: resp.target_language ?? target,
      }
      cache.set(cacheKey(texts[origIdx], target, source, format), result)
      results[origIdx] = result
    })
  }

  return results.map((r, i) =>
    r ?? { translatedText: texts[i], detectedSourceLanguage: '', targetLanguage: target },
  )
}

/**
 * Heuristic: does this text look like it might not be English (and therefore
 * worth offering for translation)?
 *
 * Signal: the presence of a non-ASCII *letter*. Requiring a Unicode letter
 * (`\p{L}`) - rather than any non-ASCII code point - fires for non-Latin
 * scripts (Japanese, Chinese, Korean, Cyrillic, Arabic, Thai, ...) and for
 * accented Latin scripts (Spanish, French, German), while deliberately ignoring
 * non-letter symbols. That matters because some fields are built from symbols:
 * e.g. a star rating like "★★★★☆ (4/5)" must NOT be treated as translatable.
 * Plain English - even with smart quotes, em dashes, or emoji - never trips it.
 */
const NON_ASCII_LETTER_RE = /\p{L}/u

export function looksTranslatable(text: string): boolean {
  if (!text) return false
  for (const ch of text) {
    const cp = ch.codePointAt(0) ?? 0
    if (cp <= 0x7f) continue // basic ASCII
    if (NON_ASCII_LETTER_RE.test(ch)) return true // a letter from another script
  }
  return false
}

/**
 * Map a few common ISO-639-1 codes to display names. Falls back to the raw
 * code (uppercased) for anything not listed - Google returns many codes and we
 * only need pretty names for the languages operators actually see.
 */
const LANGUAGE_NAMES: Record<string, string> = {
  en: 'English',
  ja: 'Japanese',
  zh: 'Chinese',
  'zh-CN': 'Chinese (Simplified)',
  'zh-TW': 'Chinese (Traditional)',
  ko: 'Korean',
  es: 'Spanish',
  pt: 'Portuguese',
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ru: 'Russian',
  ar: 'Arabic',
  hi: 'Hindi',
  th: 'Thai',
  vi: 'Vietnamese',
  id: 'Indonesian',
  tr: 'Turkish',
  pl: 'Polish',
  nl: 'Dutch',
  uk: 'Ukrainian',
}

export function languageDisplayName(code: string): string {
  if (!code) return ''
  return LANGUAGE_NAMES[code] ?? LANGUAGE_NAMES[code.split('-')[0]] ?? code.toUpperCase()
}

export type TranslationStatus = 'idle' | 'loading' | 'done' | 'error'
